import React from 'react';
import * as Icon from 'react-bootstrap-icons';

import { useTranslation } from '../../../common/hooks/useTranslation';
import { GetUmpMediaplanDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { GridColumn } from '../../../common/components/Grid/GridDataTypes';
import { Grid } from '../../../common/components/Grid/Grid';
import momencik from '../../../common/momencik';
import numberFormatter from '../../../common/numberFormatter';
import PopoverText from '../../../app/components/PopoverText';

interface UmpEstimatesGridProps {
  data: GetUmpMediaplanDto[];
  onRowClick: (key: string) => Promise<void> | void | undefined;
}

export const fieldWithError = (
  value: string | undefined,
  error: string | undefined,
) => {
  value = value || '';
  if (!error) {
    return value;
  }

  return (
    <div className="d-flex align-items-center w-100">
      <div className="flex-grow-1 text-truncate pe-2">{value}</div>
      <PopoverText
        bodyText={error}
        icon={Icon.ExclamationCircle}
        className="ms-2"
      />
    </div>
  );
};

export const UmpEstimatesGrid: React.FC<UmpEstimatesGridProps> = props => {
  const { t, tk } = useTranslation('administrationLng');
  const columns: GridColumn[] = [
    { header: t(tk.prLang.estimateName), property: 'estimateName' },
    { header: t(tk.common.startDate), property: 'startDate' },
    { header: t(tk.common.endDate), property: 'endDate' },
    { header: t(tk.common.brand), property: 'brand' },
    { header: t(tk.umpImport.purchaserUmp), property: 'purchaserUmp' },
    { header: t(tk.umpImport.purchaserPok), property: 'purchaserPok' },
    { header: t(tk.common.status), property: 'status' },
    { header: t(tk.common.countryMarket), property: 'countryMarket' },
    { header: t(tk.umpImport.totalNetBudget), property: 'totalNetBudget' },
    { header: t(tk.common.creator), property: 'creator' },
    { header: t(tk.common.creationDate), property: 'creationDate' },
  ];

  const data = props.data.map(estimate => {
    const errors = estimate.errors;
    return {
      key: estimate.uid,
      values: {
        estimateName: estimate.name,
        startDate: fieldWithError(
          momencik(estimate.startDate),
          errors?.startDate,
        ),
        endDate: fieldWithError(momencik(estimate.endDate), errors?.endDate),
        brand: fieldWithError(estimate.brandName, errors?.brandName),
        purchaserUmp: estimate.purchaserUmp,
        purchaserPok: fieldWithError(
          estimate.purchaser?.name || '',
          errors?.purchaser,
        ),
        status: estimate.status || '',
        countryMarket: estimate.countryMarket || '',
        totalNetBudget: numberFormatter(estimate.totalNetBudget || null, true),
        creator: estimate.creatorName || '',
        creationDate: momencik(estimate.creationTs),
      },
      tdCssClass: {
        totalNetBudget: 'amounts-align-right',
      },
      orderByStringValues: {
        brand: estimate.brandName || '',
        purchaserPok: estimate.purchaser?.name || '',
        startDate: momencik(estimate.startDate),
        endDate: momencik(estimate.endDate),
      },
      rowCssClass:
        estimate.errors && Object.keys(estimate.errors).length > 0
          ? 'error-row'
          : '',
    };
  });

  return (
    <Grid
      data={data}
      columns={columns}
      showFilter={true}
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.umpImport.noPositions)}
      onRowClick={props.onRowClick}
    />
  );
};
